import { TextButton, TextLink } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React from 'react';
import styled from 'styled-components';
import type { CarlineAvailableStock } from '../../graphql/types';
import { useContent } from '../context/ContentContext';
import { useFeatureAppEnv } from '../context/FeatureAppEnvContext';
import withTracking from '../hoc/withTracking';
import type { TrackingManager } from '../hooks/useTrackingManager';
import { useI18n } from '../i18n';
import { CompareCheckbox } from './compare/CompareCheckbox';

interface ActionsProps {
  readonly title: string;
  readonly group: string;
  readonly discoverLink?: string;
  readonly configureLink?: string;
  readonly availableStock?: CarlineAvailableStock;
  readonly trackingManager: TrackingManager;
  readonly carlineId: string;
}

const Actions: FC<ActionsProps> = ({
  title,
  group,
  discoverLink,
  configureLink,
  availableStock,
  trackingManager,
  carlineId,
}: ActionsProps) => {
  const { 'audi-number-formatter-service': numberFormatterService } =
    useFeatureAppEnv().featureServices;

  const availableNewCarsText =
    availableStock?.newCars &&
    useI18n('availableNewCars', {
      value: numberFormatterService.formatNumber(availableStock.newCars.amount),
    });

  const availableUsedCarsText =
    availableStock?.usedCars &&
    useI18n('availableUsedCars', {
      value: numberFormatterService.formatNumber(availableStock.usedCars.amount),
    });

  const {
    ctaDisplayOptions: { showConfigureCTA, showNewAndUsedCarsCTA, discoverLinkIsPrimaryCta },
    compareMode,
  } = useContent();

  const stockOrUsedCarsAvailable =
    availableStock !== undefined &&
    ((availableStock.newCars?.amount !== undefined && availableStock.newCars.amount > 0) ||
      (availableStock.usedCars?.amount !== undefined && availableStock.usedCars.amount > 0));

  const discoverCTALabel = useI18n('discoverCTALabel');
  const configureCTALabel = useI18n('configureCTALabel');

  const handleCtaTrackingButtonClick = (link: string, label: string, linkValue: string) => {
    trackingManager.click(link, label, linkValue, group, title);
  };

  const renderVtpInfo = (
    type: 'new' | 'used',
    amount: number | undefined,
    url: string | undefined,
    label: string | undefined,
    testId: string,
  ) => {
    const isEmpty = amount === 0;
    const spacing = 'var(--spacing-relative-md)';

    return (
      <TextLink
        data-testid={testId}
        {...(!isEmpty && url
          ? {
              href: url,
              rel: 'noopener',
              target: '_self',
            }
          : {
              type: 'button',
              disabled: true,
            })}
        margin={[spacing, 0, 0, 0]}
        size="sm"
        variant="standalone"
        onClick={() => handleCtaTrackingButtonClick(url || '', label || '', `${type} cars`)}
      >
        {label}
      </TextLink>
    );
  };

  const createCardAction = () => (
    <ActionWrapper>
      <Holder>
        {compareMode !== 'off' && configureLink !== undefined && (
          <CheckboxWrapper>
            <CompareCheckbox carlineId={carlineId} carlineName={title} />
          </CheckboxWrapper>
        )}
        {compareMode !== 'compare-only' &&
          (discoverLinkIsPrimaryCta ? (
            <>
              {discoverLink && (
                <TextButton
                  href={discoverLink}
                  variant="primary"
                  size="sm"
                  margin={[0, 0, 'var(--spacing-relative-xs)', 0]}
                  isFullWidth
                  onClick={() =>
                    handleCtaTrackingButtonClick(discoverLink, discoverCTALabel, 'discover')
                  }
                  aria-label={discoverCTALabel}
                  data-testid="primaryCta"
                >
                  {discoverCTALabel}
                </TextButton>
              )}
              {!!configureLink && showConfigureCTA && (
                <TextButton
                  href={configureLink}
                  variant="secondary"
                  size="sm"
                  margin={[0, 0, 'var(--spacing-relative-xs)', 0]}
                  isFullWidth
                  onClick={() =>
                    handleCtaTrackingButtonClick(configureLink, configureCTALabel, 'configure')
                  }
                  aria-label={configureCTALabel}
                  data-testid="secondaryCta"
                >
                  {configureCTALabel}
                </TextButton>
              )}
            </>
          ) : (
            <>
              {!!configureLink && showConfigureCTA && (
                <TextButton
                  href={configureLink}
                  variant="primary"
                  size="sm"
                  margin={[0, 0, 'var(--spacing-relative-sm)', 0]}
                  isFullWidth
                  data-testid="primaryCta"
                  onClick={() =>
                    handleCtaTrackingButtonClick(configureLink, configureCTALabel, 'configure')
                  }
                >
                  {configureCTALabel}
                </TextButton>
              )}
              {discoverLink && (
                <TextButton
                  href={discoverLink}
                  variant={showConfigureCTA ? 'secondary' : 'primary'}
                  size="sm"
                  margin={[0, 0, 'var(--spacing-relative-sm)', 0]}
                  isFullWidth
                  onClick={() =>
                    handleCtaTrackingButtonClick(discoverLink, discoverCTALabel, "'discover'")
                  }
                  aria-label={discoverCTALabel}
                  data-testid={showConfigureCTA ? 'secondaryCta' : 'primaryCta'}
                >
                  {discoverCTALabel}
                </TextButton>
              )}
            </>
          ))}
        {compareMode !== 'compare-only' && showNewAndUsedCarsCTA && stockOrUsedCarsAvailable && (
          <>
            {renderVtpInfo(
              'new',
              availableStock.newCars?.amount,
              availableStock.newCars?.url,
              availableNewCarsText ?? undefined,
              'newCarsVtpLink',
            )}

            {renderVtpInfo(
              'used',
              availableStock.usedCars?.amount,
              availableStock.usedCars?.url,
              availableUsedCarsText ?? undefined,
              'usedCarsVtpLink',
            )}
          </>
        )}
      </Holder>
    </ActionWrapper>
  );

  return <>{createCardAction()}</>;
};

export default withTracking(Actions);

const ActionWrapper = styled.div`
  /* position: relative; */
`;

const Holder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 10;
  margin-top: var(--spacing-relative-lg);
  background-color: var(--colorCanvasDefault);
`;

const CheckboxWrapper = styled.div`
  align-self: start;
`;
